// File: frontend/src/publicRoutes.js
//
// The public pages that exist as static HTML (see config/publicPages.json and
// scripts/prerender.js), and the lazy components that render them.
//
// A prerendered document arrives with its content already painted. When React
// starts, index.js preloads that page's chunk BEFORE the first render, so the
// first commit already has the page instead of flashing the Suspense spinner in
// place of content the visitor was reading. lazyWithPreload makes that possible:
// once the module is loaded, the lazy factory returns a thenable that resolves
// synchronously, and React.lazy then renders the component without suspending.

import { lazy } from "react";
import publicPages from "./config/publicPages.json";

export function lazyWithPreload(loader) {
  let loaded = null;
  const preload = () => loader().then((mod) => { loaded = mod; return mod; });
  const Component = lazy(() => (loaded ? { then: (resolve) => resolve(loaded) } : preload()));
  Component.preload = preload;
  return Component;
}

export const LandingPage     = lazyWithPreload(() => import("./pages/LandingPage"));
export const ContactPage     = lazyWithPreload(() => import("./pages/Contact"));
export const Privacy         = lazyWithPreload(() => import("./pages/Privacy"));
export const Terms           = lazyWithPreload(() => import("./pages/Terms"));
export const NotFound        = lazyWithPreload(() => import("./pages/NotFound"));
// lazy() is load-bearing here, not habitual: /ambassador is the ONLY route that
// pulls GSAP, so its own chunk keeps the animation library out of the landing
// page, the login page and the dashboard.
export const PartnersLanding = lazyWithPreload(() => import("./pages/PartnersLanding"));

const PAGE_BY_PATH = {
  "/": LandingPage,
  "/contact": ContactPage,
  "/ambassador": PartnersLanding,
  "/privacy": Privacy,
  "/terms": Terms,
};

export const INDEXABLE_PATHS = publicPages.pages.map((p) => p.path);
export const NOT_FOUND_MARKER = publicPages.notFound.marker;

/** "/contact/" and "/contact" are the same page. */
export function normalisePath(pathname) {
  return String(pathname || "/").replace(/\/+$/, "") || "/";
}

export function isIndexablePath(pathname) {
  return INDEXABLE_PATHS.includes(normalisePath(pathname));
}

/** The public page component served at `pathname`, or null if it is not one. */
export function publicPageFor(pathname) {
  return PAGE_BY_PATH[normalisePath(pathname)] || null;
}
